import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import {
  StyledCollapse,
  StyledCollapseContainer,
  StyledCollapseHeader,
  StyledCollapseLink,
  StyledDash,
  StyledParagraph,
  StyledVerticalDash,
} from './Collapse.styled'

import IconButton from '../IconButton/IconButton'
import { IconTypeEnum } from '../../theme/icons'

import useCollapseConfig from '../../hooks/useCollapseConfig'
import ButtonVariantEnum from '../../enums/ButtonVariant'

export const CollapseEnumType = Object.freeze({
  Navbar: 'navbar',
  SideNav: 'sidenav',
})

const isBrowser = typeof window !== 'undefined'

const Collapse = (props) => {
  const {
    children,
    childrenSlugs,
    childPage,
    collapseType,
    currentPage,
    iconType = IconTypeEnum.arrowDown,
    isChild,
    level,
    name,
    to,
  } = props

  const [isOpen, setIsOpen] = useState(childPage)
  const [isSelected, setIsSelected] = useState(false)
  const [hasSelectedChild, setHasSelectedChild] = useState(false)
  const collapseConfig = useCollapseConfig(
    hasSelectedChild,
    collapseType,
    iconType,
    isOpen,
    isChild,
    level,
    currentPage
  )

  useEffect(() => {
    if (isBrowser) {
      if (childrenSlugs && collapseType === CollapseEnumType.SideNav) {
        setHasSelectedChild(
          childrenSlugs.findIndex((e) =>
            window.location.pathname.includes(e.slug)
          ) !== -1
        )
      }
    }
  }, [])

  const toggleCollapse = () => setIsOpen(!isOpen)

  useMemo(() => {
    if (isBrowser) {
      if (window.location.pathname === to) {
        setIsSelected(true)
      } else {
        setIsSelected(false)
      }
    }
  }, [to])

  return (
    <StyledCollapse
      $bgColor={collapseConfig.background}
      $collapseType={collapseType}
      $isChild={isChild}
      $isOpen={isOpen || currentPage}
      $level={level}
    >
      <StyledCollapseHeader>
        {collapseType === CollapseEnumType.SideNav &&
          (!isChild ? (
            <StyledDash $isOpen={isOpen || currentPage || hasSelectedChild} />
          ) : (
            <StyledVerticalDash $isSelected={isSelected} />
          ))}
        {to !== null ? (
          <StyledCollapseLink
            to={to}
            $collapseType={collapseType}
            $color={collapseConfig.color}
            $isChild={isChild}
            $isOpen={isOpen || currentPage}
            $isSelected={isSelected}
            variant={ButtonVariantEnum.quinary}
          >
            {name}
          </StyledCollapseLink>
        ) : (
          <StyledParagraph
            $color={collapseConfig.color}
            $collapseType={collapseType}
            $isChild={isChild}
            $isOpen={isOpen || currentPage}
            $isSelected={isSelected}
            onClick={toggleCollapse}
          >
            {name}
          </StyledParagraph>
        )}
        {children && (
          <IconButton
            ariaLabel={isOpen ? 'close' : 'open'}
            onClick={toggleCollapse}
            isSvg={true}
            color={collapseConfig.iconConfig.color}
            height={collapseConfig.iconConfig.height}
            width={collapseConfig.iconConfig.width}
            type={iconType}
          >
            {collapseConfig.iconConfig.icon}
          </IconButton>
        )}
      </StyledCollapseHeader>
      {children && (
        <>
          <StyledCollapseContainer
            $isOpen={isOpen}
            $collapseType={collapseType}
          >
            {children}
          </StyledCollapseContainer>
        </>
      )}
    </StyledCollapse>
  )
}

Collapse.propTypes = {
  children: PropTypes.any,
  childPage: PropTypes.bool,
  collapseType: PropTypes.string,
  currentPage: PropTypes.bool,
  iconType: PropTypes.string,
  isChild: PropTypes.bool,
  level: PropTypes.number,
  name: PropTypes.string.isRequired,
  to: PropTypes.string,
}

export default Collapse
